import React, {
  useState, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { APIPutAssetCompliance, APIPutAssetServiceSchedule } from '../../../config/restAPI/PatchAPI';
import { APIPostAssetComplianceRenew } from '../../../config/restAPI/PostAPI';
import EditIcon from '../../../component/table/img/edit.svg';
import { useAssetCompliance } from './useAssetCompliance';
import { useAssetServiceSchedule } from './useAssetServiceSchedule';
import { useDeviceCurrentMileage } from './useDeviceCurrentMileage';
import MaintenanceComplianceScheduleModal from './MaintenanceComplianceScheduleModal';
import MaintenanceComplianceEditModal from './MaintenanceComplianceEditModal';
import MaintenanceComplianceRow, {
  formatRoadTaxValue,
  formatPuspakomValue,
  formatInsuranceValue,
} from './MaintenanceComplianceRow';
import {
  formatDate,
  hasValue,
  mapServiceScheduleToForm,
  buildServiceSchedulePayload,
  resolveNextServiceScheduleDisplay,
  sortReportsByRecency,
  isApiResponseSuccess,
  hasPersistedLastServiceFields,
  pickField,
} from './maintenanceComplianceUtils';
import {
  defaultComplianceForm,
  mapComplianceToForm,
  buildSectionCompliancePayload,
  buildSectionRenewPayload,
  hasSectionData,
  canRenewSection,
  buildBlankSectionForm,
  minRenewDateValue,
  getSectionSaveError,
  complianceModalTitles,
  validateServiceScheduleForm,
  PUSPAKOM_RESULT_OPTIONS,
  getSectionPreviousData,
} from './maintenanceComplianceFormUtils';
import {
  ComplianceStatusBadge,
  getExpiryBadge,
  getServiceOdometerBadge,
  getServiceDateBadge,
  formatKm,
  formatRemainingMileageHint,
  renderValueOrLoading,
  LOADING_LABEL,
} from './MaintenanceComplianceBadges';

const defaultServiceScheduleForm = () => mapServiceScheduleToForm({}, []);

const MaintenanceComplianceTab = ({
  selectedAsset,
  token,
  agencyID,
  formatAssetTypeLabel,
  deviceLastUpdated,
  reportLogs = [],
  reportLogsLoading = false,
}) => {
  const assetId = selectedAsset?.id;

  const {
    compliance,
    loading: complianceLoading,
    error: complianceError,
    refetch: refetchCompliance,
  } = useAssetCompliance(assetId, token, agencyID);

  const {
    serviceSchedule,
    loading: scheduleLoading,
    error: scheduleError,
    refetch: refetchServiceSchedule,
  } = useAssetServiceSchedule(assetId, token, agencyID);

  const {
    currentMileageKm,
    loading: deviceMileageLoading,
  } = useDeviceCurrentMileage(assetId, token, agencyID, selectedAsset?.mileageSource);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [complianceEditSection, setComplianceEditSection] = useState(null);
  const [scheduleForm, setScheduleForm] = useState(defaultServiceScheduleForm);
  const [complianceForm, setComplianceForm] = useState(defaultComplianceForm);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [renewSuccess, setRenewSuccess] = useState('');
  const [complianceModalMode, setComplianceModalMode] = useState('edit');
  const [renewedPreviousData, setRenewedPreviousData] = useState(null);
  const [lockedLastServiceFields, setLockedLastServiceFields] = useState({
    lastServiceDate: false,
    lastServiceOdometer: false,
  });

  const sortedReportLogs = useMemo(
    () => sortReportsByRecency(reportLogs),
    [reportLogs],
  );

  const scheduleDisplay = useMemo(
    () => resolveNextServiceScheduleDisplay(
      sortedReportLogs,
      serviceSchedule,
      currentMileageKm,
    ),
    [sortedReportLogs, serviceSchedule, currentMileageKm],
  );

  const {
    nextServiceOdometer,
    nextServiceDate,
    kmRemaining,
  } = scheduleDisplay;

  const finalizeCloseComplianceModal = useCallback(() => {
    setShowComplianceModal(false);
    setComplianceEditSection(null);
    setComplianceModalMode('edit');
    setRenewedPreviousData(null);
    setRenewSuccess('');
  }, []);

  const closeComplianceModal = useCallback(() => {
    finalizeCloseComplianceModal();
  }, [finalizeCloseComplianceModal]);

  const openScheduleModal = () => {
    setSaveError('');
    setLockedLastServiceFields(hasPersistedLastServiceFields(serviceSchedule || {}));
    setScheduleForm(mapServiceScheduleToForm(serviceSchedule || {}, sortedReportLogs));
    setShowScheduleModal(true);
  };

  const openComplianceModal = (section) => {
    setSaveError('');
    setRenewSuccess('');
    setComplianceModalMode('edit');
    setRenewedPreviousData(null);
    setComplianceForm(mapComplianceToForm(compliance || {}));
    setComplianceEditSection(section);
    setShowComplianceModal(true);
  };

  const handleSaveSchedule = async () => {
    if (!assetId) return;
    setSaveError('');

    const validationError = validateServiceScheduleForm(scheduleForm);
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    const payload = buildServiceSchedulePayload(scheduleForm);
    setSaveLoading(true);
    try {
      const result = await APIPutAssetServiceSchedule(
        token,
        () => {},
        agencyID,
        assetId,
        payload,
      );
      if (isApiResponseSuccess(result)) {
        setShowScheduleModal(false);
        await refetchServiceSchedule();
        return;
      }
      const message = result?.response?.data?.message || result?.data?.message;
      setSaveError(message || 'Failed to save service schedule.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSaveCompliance = async () => {
    if (!assetId) return;
    setSaveError('');
    setRenewSuccess('');

    if (!complianceEditSection) {
      setSaveError('Unable to save: no compliance section selected.');
      return;
    }

    const validationError = getSectionSaveError(
      complianceEditSection,
      complianceForm,
      complianceModalMode,
      renewedPreviousData,
    );
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    const isRenew = complianceModalMode === 'add' && renewedPreviousData !== null;
    const payload = isRenew
      ? buildSectionRenewPayload(complianceEditSection, complianceForm)
      : buildSectionCompliancePayload(complianceEditSection, complianceForm);

    setSaveLoading(true);
    try {
      const result = isRenew
        ? await APIPostAssetComplianceRenew(
          token,
          () => {},
          agencyID,
          assetId,
          payload,
        )
        : await APIPutAssetCompliance(
          token,
          () => {},
          agencyID,
          assetId,
          payload,
        );
      if (isApiResponseSuccess(result)) {
        finalizeCloseComplianceModal();
        await refetchCompliance();
        return;
      }
      const message = result?.response?.data?.message || result?.data?.message;
      setSaveError(message || (isRenew ? 'Failed to renew compliance record.' : 'Failed to save vehicle compliance.'));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleRenewSection = (section) => {
    if (!assetId || !section) return;
    setSaveError('');
    setRenewSuccess('');

    const form = mapComplianceToForm(compliance || {});
    if (!hasSectionData(section, form)) {
      setComplianceModalMode('add');
      setRenewedPreviousData(null);
      setComplianceForm(buildBlankSectionForm(section));
      setComplianceEditSection(section);
      setShowComplianceModal(true);
      return;
    }

    if (!canRenewSection(section, form)) {
      setComplianceModalMode('edit');
      setRenewedPreviousData(null);
      setComplianceForm(form);
      setComplianceEditSection(section);
      setShowComplianceModal(true);
      setSaveError('Complete the current due/expiry date before renewing.');
      return;
    }

    setComplianceForm(buildBlankSectionForm(section));
    setComplianceEditSection(section);
    setComplianceModalMode('add');
    setRenewedPreviousData(getSectionPreviousData(section, compliance));
    setShowComplianceModal(true);
    setRenewSuccess('Enter the new details below. The current record will be archived when you save.');
  };

  const roadTaxExpiry = pickField(compliance, 'roadTaxExpiry', 'road_tax_expiry');
  const puspakom = compliance?.puspakom || compliance?.puspaKom || {};
  const insurance = compliance?.insurance || {};
  const puspakomNextDue = pickField(puspakom, 'nextDue', 'next_due');
  const insuranceExpiry = pickField(insurance, 'expiry');

  const puspakomResultOptions = PUSPAKOM_RESULT_OPTIONS.map((item) => ({ label: item, value: item }));
  const selectedPuspakomResult = complianceForm.puspakomResult
    ? puspakomResultOptions.find((opt) => opt.value === complianceForm.puspakomResult)
      || { label: complianceForm.puspakomResult, value: complianceForm.puspakomResult }
    : null;

  const scheduleDataLoading = reportLogsLoading || scheduleLoading;
  const currentMileageLoading = deviceMileageLoading;
  const hasScheduleDisplay = hasValue(nextServiceOdometer) || hasValue(nextServiceDate);
  const showScheduleLoading = scheduleDataLoading && !hasScheduleDisplay;
  const remainingMileageHint = formatRemainingMileageHint(kmRemaining);
  const odometerBadge = getServiceOdometerBadge(kmRemaining);
  const dateBadge = getServiceDateBadge(nextServiceDate);
  const complianceModalTitle = complianceEditSection
    ? complianceModalTitles[complianceModalMode][complianceEditSection]
    : '';
  const renewMinDate = minRenewDateValue();
  const useRenewDateRules = complianceModalMode === 'add' && renewedPreviousData !== null;

  return (
    <div className="maintenance-compliance-tab">
      <div className="maintenance-detail-card">
        <div className="maintenance-detail-row">
          <span className="maintenance-detail-label">Asset name</span>
          <span className="maintenance-detail-value">
            {selectedAsset.assetName || selectedAsset.deviceSN}
          </span>
        </div>
        <div className="maintenance-detail-row">
          <span className="maintenance-detail-label">Asset type</span>
          <span className="maintenance-detail-value">{formatAssetTypeLabel(selectedAsset.assetType)}</span>
        </div>
        <div className="maintenance-detail-row">
          <span className="maintenance-detail-label">Device last update</span>
          <span className="maintenance-detail-value">{deviceLastUpdated || '-'}</span>
        </div>
        <div className="maintenance-detail-row">
          <span className="maintenance-detail-label">Current mileage</span>
          <span className="maintenance-detail-value">
            {currentMileageLoading
              ? LOADING_LABEL
              : (hasValue(currentMileageKm) ? formatKm(currentMileageKm) : '-')}
          </span>
        </div>
      </div>

      <div className="maintenance-compliance-section">
        <div className="maintenance-compliance-section-header">
          <h3 className="maintenance-compliance-section-title">Next Service Schedule</h3>
          <button type="button" className="maintenance-compliance-edit-btn" onClick={openScheduleModal}>
            <img src={EditIcon} alt="" className="maintenance-compliance-edit-icon" />
            Edit
          </button>
        </div>
        {scheduleError && (
          <div className="maintenance-compliance-inline-error">{scheduleError}</div>
        )}
        {showScheduleLoading ? (
          <div className="maintenance-compliance-loading">Loading service schedule…</div>
        ) : (
          <>
            <div className="maintenance-compliance-row maintenance-compliance-row--schedule maintenance-compliance-row--mileage">
              <span className="maintenance-compliance-row-label">Service Mileage</span>
              <div className="maintenance-compliance-row-value-wrap">
                <div className="maintenance-compliance-row-value-stack">
                  <span className="maintenance-compliance-row-value">
                    {renderValueOrLoading(
                      scheduleDataLoading,
                      nextServiceOdometer,
                      (value) => formatKm(value),
                    )}
                  </span>
                  {!scheduleDataLoading && !currentMileageLoading && remainingMileageHint && (
                    <span className="maintenance-current-mileage-hint">
                      {remainingMileageHint}
                    </span>
                  )}
                </div>
                <ComplianceStatusBadge
                  variant={odometerBadge.variant}
                  label={odometerBadge.label}
                />
              </div>
            </div>
            <div className="maintenance-compliance-row maintenance-compliance-row--schedule">
              <span className="maintenance-compliance-row-label">Service Date</span>
              <div className="maintenance-compliance-row-value-wrap">
                <span className="maintenance-compliance-row-value">
                  {renderValueOrLoading(
                    scheduleDataLoading,
                    nextServiceDate,
                    (value) => formatDate(value),
                  )}
                </span>
                <ComplianceStatusBadge
                  variant={dateBadge.variant}
                  label={dateBadge.label}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="maintenance-compliance-section">
        <div className="maintenance-compliance-section-header">
          <h3 className="maintenance-compliance-section-title">Vehicle Compliance</h3>
        </div>
        {complianceError && (
          <div className="maintenance-compliance-inline-error">{complianceError}</div>
        )}
        {complianceLoading ? (
          <div className="maintenance-compliance-loading">Loading vehicle compliance…</div>
        ) : (
          <>
            <MaintenanceComplianceRow
              label="Road Tax"
              valueText={formatRoadTaxValue(roadTaxExpiry)}
              badge={getExpiryBadge(roadTaxExpiry)}
              section="roadTax"
              saveLoading={saveLoading}
              onRenew={handleRenewSection}
              onEdit={openComplianceModal}
            />
            <MaintenanceComplianceRow
              label="PUSPAKOM Inspection"
              valueText={formatPuspakomValue(puspakomNextDue)}
              badge={getExpiryBadge(puspakomNextDue)}
              section="puspakom"
              saveLoading={saveLoading}
              onRenew={handleRenewSection}
              onEdit={openComplianceModal}
            />
            <MaintenanceComplianceRow
              label="Insurance"
              valueText={formatInsuranceValue(insuranceExpiry)}
              badge={getExpiryBadge(insuranceExpiry)}
              section="insurance"
              saveLoading={saveLoading}
              onRenew={handleRenewSection}
              onEdit={openComplianceModal}
            />
          </>
        )}
      </div>

      {showScheduleModal && (
        <MaintenanceComplianceScheduleModal
          scheduleForm={scheduleForm}
          lockedLastServiceFields={lockedLastServiceFields}
          saveError={saveError}
          saveLoading={saveLoading}
          onClose={() => setShowScheduleModal(false)}
          onSave={handleSaveSchedule}
          onFormChange={(key, value) => setScheduleForm((prev) => ({ ...prev, [key]: value }))}
        />
      )}

      {showComplianceModal && complianceEditSection && (
        <MaintenanceComplianceEditModal
          title={complianceModalTitle}
          section={complianceEditSection}
          complianceForm={complianceForm}
          saveError={saveError}
          renewSuccess={renewSuccess}
          saveLoading={saveLoading}
          renewMinDate={renewMinDate}
          useRenewDateRules={useRenewDateRules}
          selectedPuspakomResult={selectedPuspakomResult}
          onClose={closeComplianceModal}
          onSave={handleSaveCompliance}
          onFormChange={(key, value) => setComplianceForm((prev) => ({ ...prev, [key]: value }))}
        />
      )}

    </div>
  );
};

MaintenanceComplianceTab.propTypes = {
  selectedAsset: PropTypes.shape({
    id: PropTypes.string,
    assetName: PropTypes.string,
    deviceSN: PropTypes.string,
    assetType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mileageSource: PropTypes.string,
  }).isRequired,
  token: PropTypes.string,
  agencyID: PropTypes.string,
  formatAssetTypeLabel: PropTypes.func.isRequired,
  deviceLastUpdated: PropTypes.string,
  reportLogs: PropTypes.arrayOf(PropTypes.shape({
    rawRecord: PropTypes.shape({
      receiptInfo: PropTypes.shape({}),
      assetIdentification: PropTypes.shape({}),
    }),
  })),
  reportLogsLoading: PropTypes.bool,
};

MaintenanceComplianceTab.defaultProps = {
  token: '',
  agencyID: '',
  deviceLastUpdated: '',
  reportLogs: [],
  reportLogsLoading: false,
};

export default MaintenanceComplianceTab;
